import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, buildQuery, type AuditEntry, type Page } from '../lib/api';
import { useAsync, useDebounced } from '../lib/hooks';
import { fmtDateTime } from '../lib/format';
import { ErrorAlert, PageHeader, Pagination, Spinner } from '../components/ui';
import { useDict, useT } from '../i18n';

/** Prefijos de acción por los que se puede filtrar (clave de traducción → prefijo en la API). */
const GROUPS = [
  ['license', 'license.'],
  ['activation', 'activation.'],
  ['floating', 'floating.'],
  ['trial', 'trial.'],
  ['product', 'product.'],
  ['edition', 'edition.'],
  ['customer', 'customer'],
  ['webhook', 'webhook.'],
] as const;
const ACTORS = ['admin', 'client', 'system'] as const;

export function AuditTable({ rows, compact }: { rows: AuditEntry[]; compact?: boolean }) {
  const t = useT();
  const dict = useDict();
  const actionLabel = (a: string) => (dict.audit.action as Record<string, string>)[a] ?? a;
  const actorLabel = (a: string) => (dict.audit.actor as Record<string, string>)[a] ?? a;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('common.date')}</th>
            <th>{t('audit.colActor')}</th>
            <th>{t('audit.colAction')}</th>
            {!compact && <th>{t('audit.colEntity')}</th>}
            <th>{t('audit.colDetails')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={compact ? 4 : 5} className="empty">{t('audit.empty')}</td></tr>}
          {rows.map((a) => (
            <tr key={a.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.at)}</td>
              <td>
                <span className={`badge plain ${a.actor_type === 'admin' ? 'info' : ''}`}>{actorLabel(a.actor_type)}</span>
                <div className="muted" style={{ fontSize: 12 }}>{a.actor_id}{a.ip ? ` · ${a.ip}` : ''}</div>
              </td>
              <td>{actionLabel(a.action)}</td>
              {!compact && <td>{a.license_id ? <Link to={`/licencias/${a.license_id}`}>{a.entity_type}</Link> : a.entity_type}</td>}
              <td className="mono muted" style={{ fontSize: 12, maxWidth: 360, overflowWrap: 'anywhere' }}>
                {Object.keys(a.data).length ? JSON.stringify(a.data) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AuditPage() {
  const t = useT();
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState('');
  const [actorId, setActorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const dActor = useDebounced(actorId);
  const list = useAsync(
    () =>
      api.get<Page<AuditEntry>>(
        `/v1/admin/audit${buildQuery({
          action,
          actor_type: actorType,
          actor_id: dActor,
          from: from ? new Date(from).toISOString() : '',
          to: to ? new Date(`${to}T23:59:59`).toISOString() : '',
          page,
          page_size: 50,
        })}`,
      ),
    [action, actorType, dActor, from, to, page],
  );
  return (
    <>
      <PageHeader title={t('audit.title')} subtitle={t('audit.subtitle')} />
      <ErrorAlert error={list.error} />
      <div className="card">
        <div className="toolbar">
          <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} aria-label={t('audit.colAction')}>
            <option value="">{t('audit.allActions')}</option>
            {GROUPS.map(([key, prefix]) => (
              <option key={key} value={prefix}>{t(`audit.group.${key}`)}</option>
            ))}
          </select>
          <select value={actorType} onChange={(e) => { setActorType(e.target.value); setPage(1); }} aria-label={t('audit.colActor')}>
            <option value="">{t('audit.anyActor')}</option>
            {ACTORS.map((a) => (
              <option key={a} value={a}>{t(`audit.actorFilter.${a}`)}</option>
            ))}
          </select>
          <input placeholder={t('audit.userPlaceholder')} value={actorId} onChange={(e) => { setActorId(e.target.value); setPage(1); }} />
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} aria-label={t('audit.from')} />
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} aria-label={t('audit.to')} />
          {list.loading && <Spinner />}
        </div>
        {list.data && (
          <>
            <AuditTable rows={list.data.items} />
            <Pagination page={page} pageSize={50} total={list.data.total} onPage={setPage} />
          </>
        )}
      </div>
    </>
  );
}
